import * as ecStat from "echarts-stat";

import moment from "moment";

import getLogger from "utils/logger.util";
import { ChartStrings } from "constants/constantStrings";

import ChartConstant from "../../../constants/chartConstant";
import ChartUtil from "../../../utils/ChartUtil";

const Logger = getLogger("Chart - AlarmUtil");

const { CHART_LINES_CONFIG } = ChartUtil;

const getFormattedTagsData = (Tags, xAxisData) =>
  Tags.map(tag => {
    const { TagName, Data } = tag;
    return {
      x: xAxisData,
      y: Data,
      name: TagName,
      meta: TagName,
      fixedrange: true,
      ...ChartUtil.getTagConfig(tag,false)
    };
  });

const getAxisTitleFont = color => ({ color, family: "Noto Sans", size: 14 });

const getMultiYChartYAxisObj = (title, color) => ({
  title,
  titlefont: getAxisTitleFont(color),
  tickfont: { color, size: 10 },
  showgrid: false,
  ...CHART_LINES_CONFIG
});

const getMultiYAxisPosition = (tagsCount, index) => {
  let tagAxisPosition = 0.04;
  if (tagsCount === 5) {
    tagAxisPosition = index === 1 ? 0.28 : index === 2 ? 0.20 : index === 3 ? 0.12 : index === 4 && 0.04;
  } else if (tagsCount === 4) {
    tagAxisPosition = index === 1 ? 0.20 : index === 2 ? 0.12 : index === 3 && 0.04;
  } else if (tagsCount === 3) {
    tagAxisPosition = index === 1 ? 0.12 : index === 2 && 0.04;
  }
  return tagAxisPosition;
};

const getSinglePlotLayoutConfig = (chartTagsData, range, PlotZoomData, tagsforlimits) => {
   // Extract lower and upper limits from tags, filtering out null values.
   const lowerlimits = tagsforlimits.map(tag => tag.Ylower).filter(limit => limit != null);
   const upperlimits = tagsforlimits.map(tag => tag.Yupper).filter(limit => limit != null);
   
   // Get minimum lower limit and maximum upper limit, or null if none exist.
   const lowerlimit = lowerlimits.length > 0 ? Math.min(...lowerlimits) : null;
   const upperlimit = upperlimits.length > 0 ? Math.max(...upperlimits) : null;
   
   // Flatten data points from all tags and determine min/max data values.
   const allDataPoints = chartTagsData.flatMap(tag => tag.Data);
   const minDataValue = Math.min(...allDataPoints); // Minimum data value.
   const maxDataValue = Math.max(...allDataPoints); // Maximum data value.
   
  const chartLayout = {
    xaxis: { type: "date", ...CHART_LINES_CONFIG, range },
    // yaxis: { automargin: true, tickformat: '.2f', ...CHART_LINES_CONFIG },
    yaxis: { automargin: true, ...CHART_LINES_CONFIG },
    margin: { l: 20, r: 20, b: 20, t: 35, pad: 5 },
    showlegend: true
  };
  chartLayout.yaxis.range = ChartUtil.getYAxisRange(lowerlimit, upperlimit, minDataValue, maxDataValue);
  const firstTagUnit = chartTagsData[0].Units;
  const isSameUnit = chartTagsData.every(tag => tag.Units === firstTagUnit);
  if (isSameUnit) {
    chartLayout.yaxis.title = { text: firstTagUnit, standoff: 10 };
  }
  if (PlotZoomData?.yRange) {
    chartLayout.yaxis.range = PlotZoomData.yRange;
  }
  return chartLayout;
};
const tagdistanceVariable = [0.08, 0.08, 0.13, 0.11, 0.1, 0.095];
const getMultiYPlotLayoutConfig = (chartTagsData, range, PlotZoomData, tagsforlimits) => {
  const tagsCount = chartTagsData.length;
  const xVal = tagdistanceVariable[tagsCount] * (tagsCount - 1);
  const domainArr = [+xVal.toFixed(2), 1.0];
  let chartLayout = {
    xaxis: { type: "date", ...CHART_LINES_CONFIG, range },
    margin: { l: 20, r: 20, b: 20, t: 35, pad: 5 },
    legend: { y: -0.2, orientation: "h" }
  };
  if (tagsCount === 1) {
    const yaxis = getMultiYChartYAxisObj(chartTagsData[0].Units, chartTagsData[0].Color);
    chartLayout = {
      ...chartLayout,
      yaxis: { ...yaxis, automargin: true, ...CHART_LINES_CONFIG, tickangle: -45 },
      showlegend: true
    };
  } else {
    chartLayout = { ...chartLayout, xaxis: { ...chartLayout.xaxis, domain: domainArr, showgrid: true } };

    chartTagsData.forEach((tag, index) => {
      const yaxis = getMultiYChartYAxisObj(tag.Units, tag.Color);
      const lowerlimit = tagsforlimits[index].Ylower;
      const upperlimit = tagsforlimits[index].Yupper;
      const allDataPoints = tag.Data;
      const minDataValue = Math.min(...allDataPoints);
      const maxDataValue = Math.max(...allDataPoints);
      if (PlotZoomData?.yRange) {
        const position = index * 2;
        yaxis.range = [PlotZoomData.yRange[position], PlotZoomData.yRange[position + 1]];
      }
      if (index === 0) {
        chartLayout.yaxis = yaxis;
        chartLayout.yaxis.tickangle = -45;
        chartLayout.yaxis.range = ChartUtil.getYAxisRange(lowerlimit, upperlimit, minDataValue, maxDataValue);
      } else {
        const yaxisNumber = `yaxis${index + 1}`;
        chartLayout[yaxisNumber] = {
          ...yaxis,
          anchor: "free",
          overlaying: "y",
          side: "left",
          position: getMultiYAxisPosition(tagsCount, index),
          tickangle: -45
        };
        chartLayout[yaxisNumber].range = ChartUtil.getYAxisRange(lowerlimit, upperlimit, minDataValue, maxDataValue);
      }
    });
  }
  return chartLayout;
};

export const calculateTrendLine = (xAxisData, tag, index, showlegend) => {
  const { Data, Units, Trendline } = tag;
  let trendData = null;
  if (Trendline) {
    const polynom = 3;
    let previousNonNulVal;
    let startIndexWithVal = null;
    const chartData = Data.map((data, idx) => {
      if (isNaN(parseFloat(data))) {
        data = previousNonNulVal;
      } else {
        previousNonNulVal = data;
        if (startIndexWithVal === null) {
          startIndexWithVal = idx;
        }
      }
      return [idx, data];
    });
    const regression = ecStat.regression("polynomial", chartData, polynom);
    const trendingYData = regression.points.map(([, point]) => parseFloat(point?.toFixed(4)));
    trendData = {
      x: xAxisData.slice(startIndexWithVal, xAxisData.length), // remove starting x axis point for initial null values
      y: trendingYData,
      hovertemplate: ` <b>%{y:,.2f} (${Units}) %{x|%d %b %Y, %H:%M:%S} </b><br>%{meta}<extra></extra>`,
      showlegend,
      xaxis: `x${index + 1}`,
      yaxis: `y${index + 1}`,
      mode: "lines",
      line: { width: 1.5, color: "black" },
      name: ChartStrings.chartTrendLine,
      meta: ChartStrings.chartTrendLine
    };
  }
  return trendData;
};

export const parseChartData = (Format, TagsToShow, PlotData, zoomData, Layout, ChartXAxisRange, PlotZoomData, tagsforLimits) => {
  Logger.log("onPlotZoom parseChartData - ChartXAxisRange: ", zoomData, ChartXAxisRange, PlotZoomData);
  let ChartPlot = { Layout: {}, Data: [] };
  const { Time, ...TagsData } = PlotData;
  if (TagsToShow.length && Time) {
    const chartTagsData = TagsToShow.map(tag => {
      const tagData = TagsData[tag.TagId];
      return { ...tag, Data: tagData || [] };
    });
    
    let tagsforlimits = []; 
    if (tagsforLimits)  {
    tagsforlimits = tagsforLimits.filter(limitTag => 
        TagsToShow.some(showTag => showTag.TagId === limitTag.TagId)
      );
    }
    const xAxisData = Time;
    const Data = getFormattedTagsData(chartTagsData, xAxisData);
    switch (Format) {
      case ChartConstant.CHART_PLOT_FORMATS.MULTI_PLOT: {
        Layout = { ...Layout, ...ChartUtil.getMultiPlotLayoutConfig(chartTagsData, ChartXAxisRange, null, PlotZoomData, tagsforlimits) };
        Data.forEach((tag, index) => {
          if (index > 0 && tag.mode !== "text") {
            Data[index].xaxis = `x${index + 1}`;
            Data[index].yaxis = `y${index + 1}`;
          }
        });

        let isTrendLegendDisplayed = false;
        const trends = chartTagsData.reduce((result, tag, index) => {
          if (tag.Trendline) {
            const showLegend = tag.Trendline && !isTrendLegendDisplayed;
            const data = calculateTrendLine(xAxisData, tag, index, showLegend);
            if (data) {
              if (showLegend) isTrendLegendDisplayed = true;
              result.push(data);
            }
          }
          return result;
        }, []);
        if (trends && trends.length) {
          Data.push(...trends);
        }

        break;
      }
      case ChartConstant.CHART_PLOT_FORMATS.MULTI_Y_PLOT: {
        Layout = { ...Layout, ...getMultiYPlotLayoutConfig(chartTagsData, ChartXAxisRange, PlotZoomData, tagsforlimits) };
        Data.forEach((tag, index) => {
          if (index > 0 && tag.mode !== "text") {
            Data[index].yaxis = `y${index + 1}`;
          }
        });
        break;
      }
      default: {
        Layout = { ...Layout, ...getSinglePlotLayoutConfig(chartTagsData, ChartXAxisRange, PlotZoomData, tagsforlimits) };
        if (chartTagsData.length === 1) {
          if (chartTagsData[0].Trendline) {
            const trend = calculateTrendLine(xAxisData, chartTagsData[0], 0, true);
            Data.push(trend);
          }
        }
        break;
      }
    }
    ChartPlot = { Layout, Data };
  }
  return ChartPlot;
};

const getDateIndex = (TimeData, date) => {
  date = `${date}:00`.replace(/\s/, "T").slice(0, 19) + "+00:00";
  const limitTime = moment(new Date(date)).unix();
  return TimeData.findIndex(time => {
    const timeStamp = moment(new Date(time)).unix();
    return timeStamp >= limitTime;
  });
};

export const parseZoomData = (Format, TimeData, zoomEvent) => {
  let zoomData = null;
  if (typeof zoomEvent === "object") {
    const xAxisChange = Object.fromEntries(Object.entries(zoomEvent).filter(([key]) => key.includes("xaxis")));
    const yAxisChange = Object.fromEntries(Object.entries(zoomEvent).filter(([key]) => key.includes("yaxis")));
    if (Object.keys(xAxisChange).length > 0) {
      if (Object.keys(xAxisChange).includes("xaxis.autorange")) {
        Logger.info("parseZoomData - reset to axis ");
      } else {
        const xRange = Object.values(xAxisChange);
        const yRange = Object.values(yAxisChange);
        const startIndex = getDateIndex(TimeData, xRange[0]);
        const endIndex = startIndex + getDateIndex(TimeData.slice(startIndex), xRange[1]) + 1;
        zoomData = { startIndex, yRange, endIndex, xRange };
      }
    }
    return zoomData;
  }
};
